import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, homeForRole } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.pexels.com/photos/2348359/pexels-photo-2348359.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { user, login, driverLogin, driverClaim } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("staff"); // staff | driver
  const [claimMode, setClaimMode] = useState(false);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [dpassword, setDpassword] = useState("");
  const [dname, setDname] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate(homeForRole(user.role), { replace: true });
  }, [user, navigate]);

  const finish = (res) => {
    setLoading(false);
    if (res.ok) {
      toast.success(res.role === "driver" ? "Welcome!" : "Welcome back");
      navigate(homeForRole(res.role), { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  const submitStaff = async (e) => {
    e.preventDefault();
    setLoading(true);
    finish(await login(email, password));
  };

  const submitDriver = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (claimMode) finish(await driverClaim(phone, dpassword, dname));
    else finish(await driverLogin(phone, dpassword));
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-slate-950 lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#A0148C]">
              <Truck className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-black uppercase tracking-tight text-slate-50">
                Discount Truck Parking
              </h1>
              <p className="text-sm font-bold tracking-[0.2em] text-[#A0148C]">truckstopdiscount.com</p>
            </div>
          </div>

          {/* Audience toggle */}
          <div className="mb-6 grid grid-cols-2 gap-1 rounded-xl bg-slate-900 p-1">
            <button
              type="button"
              data-testid="login-tab-staff"
              onClick={() => { setTab("staff"); setClaimMode(false); }}
              className={`rounded-lg py-2.5 text-sm font-bold transition-colors ${tab === "staff" ? "bg-[#A0148C] text-white" : "text-slate-400 hover:text-slate-200"}`}
            >
              Owner / Staff
            </button>
            <button
              type="button"
              data-testid="login-tab-driver"
              onClick={() => setTab("driver")}
              className={`rounded-lg py-2.5 text-sm font-bold transition-colors ${tab === "driver" ? "bg-[#A0148C] text-white" : "text-slate-400 hover:text-slate-200"}`}
            >
              Driver
            </button>
          </div>

          {tab === "staff" ? (
            <>
              <h2 className="mb-2 font-heading text-3xl font-black tracking-tight text-slate-50">Yard Command Center</h2>
              <p className="mb-8 text-slate-400">Sign in to manage your lots in real time.</p>
              <form onSubmit={submitStaff} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email</Label>
                  <Input id="email" type="email" data-testid="login-email-input" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-14 border-slate-700 bg-slate-900 text-lg focus:border-[#A0148C] focus:ring-[#A0148C]" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">Password</Label>
                  <Input id="password" type="password" data-testid="login-password-input" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-14 border-slate-700 bg-slate-900 text-lg focus:border-[#A0148C] focus:ring-[#A0148C]" required />
                </div>
                <Button type="submit" data-testid="login-submit-button" disabled={loading}
                  className="h-14 w-full bg-[#A0148C] text-lg font-bold hover:bg-[#80106F]">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="mb-2 font-heading text-3xl font-black tracking-tight text-slate-50">
                {claimMode ? "Set up your account" : "Driver Sign In"}
              </h2>
              <p className="mb-8 text-slate-400">
                {claimMode
                  ? "Enter the phone number you gave at check-in and choose a password."
                  : "Track your stay, renew, and see your rewards."}
              </p>
              <form onSubmit={submitDriver} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-300">Phone number</Label>
                  <Input id="phone" type="tel" data-testid="driver-phone-input" value={phone}
                    onChange={(e) => setPhone(e.target.value)} placeholder="954-555-0100"
                    className="h-14 border-slate-700 bg-slate-900 text-lg focus:border-[#A0148C] focus:ring-[#A0148C]" required />
                </div>
                {claimMode && (
                  <div className="space-y-2">
                    <Label htmlFor="dname" className="text-slate-300">Your name (optional)</Label>
                    <Input id="dname" data-testid="driver-name-input" value={dname}
                      onChange={(e) => setDname(e.target.value)}
                      className="h-14 border-slate-700 bg-slate-900 text-lg focus:border-[#A0148C] focus:ring-[#A0148C]" />
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="dpassword" className="text-slate-300">{claimMode ? "Create password" : "Password"}</Label>
                  <Input id="dpassword" type="password" data-testid="driver-password-input" value={dpassword}
                    onChange={(e) => setDpassword(e.target.value)}
                    className="h-14 border-slate-700 bg-slate-900 text-lg focus:border-[#A0148C] focus:ring-[#A0148C]" required />
                </div>
                <Button type="submit" data-testid="driver-submit-button" disabled={loading}
                  className="h-14 w-full bg-[#A0148C] text-lg font-bold hover:bg-[#80106F]">
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : claimMode ? "Set Password & Enter" : "Sign In"}
                </Button>
              </form>
              <button type="button" data-testid="driver-claim-toggle" onClick={() => setClaimMode((v) => !v)}
                className="mt-5 text-sm font-semibold text-[#A0148C] hover:underline">
                {claimMode ? "← Back to sign in" : "First time here? Set up your password"}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="relative hidden lg:block">
        <img src={BG} alt="Truck fleet at night" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="absolute bottom-0 left-0 p-12">
          <p className="font-heading text-4xl font-black leading-tight text-white">
            Replace the whiteboard.<br />
            <span className="text-[#A0148C]">Run the whole yard live.</span>
          </p>
          <p className="mt-4 max-w-md text-slate-300">
            Live spot visibility, automated billing, and overstay collection across every truck stop.
          </p>
        </div>
      </div>
    </div>
  );
}
